import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  Timestamp,
  doc,
  getDoc,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../firebase';

// Inventory Transaction interface (from main-app)
export interface InventoryTransaction {
  id?: string;
  productId: string;
  date: Date | Timestamp;
  type: 'addition' | 'reduction' | 'purchase' | 'sale';
  quantity: number;
  previousStock: number;
  newStock: number;
  reason: string;
  performedBy: string;
  performedById: string;
  referenceId?: string;
}

// Mapped Inventory Adjustment interface for UI
export interface InventoryAdjustment {
  id?: string;
  adjustmentDate: Date | Timestamp;
  adjustmentNumber: string;
  reason: string;
  notes?: string;
  totalValueChange: number;
  items: InventoryAdjustmentItem[];
  createdAt?: Date | Timestamp;
  updatedAt?: Date | Timestamp;
  createdBy?: string;
  // Additional fields for transaction filtering
  type?: 'addition' | 'reduction' | 'purchase' | 'sale';
  subtype?: string;
  transactionData?: {
    type?: string;
    subtype?: string;
  };
}

// Inventory Adjustment Item interface
export interface InventoryAdjustmentItem {
  productId: string;
  productName: string;
  productSku?: string;
  quantityBefore: number;
  quantityAfter: number;
  quantityChange: number;
  averageCost: number;
  valueChange: number;
  subtype?: 'manual_addition' | 'void_sale_restoration' | 'purchase_receipt' | 'manual_reduction' | 'damage' | 'expiry' | 'missing' | 'sale_deduction' | 'sample' | 'salon_use';
}

// Get inventory adjustments with optional pagination and filtering
export const getInventoryAdjustments = async (
  limitCount?: number,
  startAfterDate?: Date | Timestamp,
  filterType?: 'positive' | 'negative' | 'all'
): Promise<InventoryAdjustment[]> => {
  try {
    // Get all products first to have product names and costs
    const productsSnapshot = await getDocs(collection(db, 'products'));
    const productsMap: Record<string, { name: string, sku: string, averageCost: number }> = {};
    
    // Also get product costs from productCosts collection for more accurate cost data
    const productCostsSnapshot = await getDocs(collection(db, 'productCosts'));
    const productCostsMap: Record<string, number> = {};
    
    productCostsSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.averageCost) {
        productCostsMap[doc.id] = data.averageCost;
      }
    });
    
    productsSnapshot.forEach((doc) => {
      const data = doc.data();
      // Use the cost from productCosts if available, otherwise fallback to product.averageCost
      // If neither exists, use a default cost of 10 (better than 0 for display purposes)
      const averageCost = productCostsMap[doc.id] || data.averageCost || 10;
      
      productsMap[doc.id] = {
        name: data.name || 'Unknown Product',
        sku: data.sku || 'N/A',
        averageCost: averageCost
      };
    });
    
    // Query inventory transactions
    let transactionsQuery = query(
      collection(db, 'inventoryTransactions'),
      orderBy('date', 'desc')
    );
    
    // Get all transaction types (addition, reduction, sale, adjustment)
    // No type filter applied here to get all transactions
    
    // Apply filter by adjustment type if specified
    if (filterType === 'positive') {
      transactionsQuery = query(
        transactionsQuery,
        where('type', '==', 'addition')
      );
    } else if (filterType === 'negative') {
      transactionsQuery = query(
        transactionsQuery,
        where('type', '==', 'reduction')
      );
    }
    
    // Apply pagination if start date is provided
    if (startAfterDate) {
      const timestamp = startAfterDate instanceof Date 
        ? Timestamp.fromDate(startAfterDate) 
        : startAfterDate;
      transactionsQuery = query(
        transactionsQuery,
        startAfter(timestamp)
      );
    }
    
    // Apply limit if specified
    if (limitCount) {
      transactionsQuery = query(
        transactionsQuery,
        limit(limitCount)
      );
    }
    
    const snapshot = await getDocs(transactionsQuery);
    const adjustments: InventoryAdjustment[] = [];
    
    snapshot.forEach((doc) => {
      const transaction = doc.data() as InventoryTransaction;
      const product = productsMap[transaction.productId] || { name: 'Unknown Product', sku: 'N/A', averageCost: 0 };
      
      // Calculate quantity change
      const quantityChange = transaction.type === 'addition' ? transaction.quantity : -transaction.quantity;
      
      // Different value calculation based on transaction type
      let valueChange = 0;
      const effectiveAverageCost = product.averageCost || 10; // Use default cost of 10 if not available
      
      if (transaction.type === 'reduction') {
        // For reductions (write-offs, damages, missing items), show financial impact
        // This represents the cost to the business
        valueChange = quantityChange * effectiveAverageCost;
      } else if (transaction.reason && transaction.reason.includes('Purchase Receipt')) {
        // For purchase receipts, calculate the value change based on average cost
        valueChange = quantityChange * effectiveAverageCost;
      }
      // For free product additions and other additions, keep value change as 0
      
      // Map transaction to adjustment format
      const adjustment: InventoryAdjustment = {
        id: doc.id,
        adjustmentDate: transaction.date,
        adjustmentNumber: doc.id.substring(0, 8).toUpperCase(),
        reason: transaction.reason,
        totalValueChange: valueChange,
        createdBy: transaction.performedBy,
        items: [{
          productId: transaction.productId,
          productName: product.name,
          productSku: product.sku,
          quantityBefore: transaction.previousStock,
          quantityAfter: transaction.newStock,
          quantityChange: quantityChange,
          averageCost: product.averageCost || 0,
          valueChange: valueChange
        }]
      };
      
      adjustments.push(adjustment);
    });
    
    return adjustments;
  } catch (error) {
    console.error('Error getting inventory adjustments:', error);
    throw error;
  }
};

// Get inventory adjustment by ID
export const getInventoryAdjustmentById = async (
  adjustmentId: string
): Promise<InventoryAdjustment | null> => {
  try {
    // Get the transaction
    const docRef = doc(db, 'inventoryTransactions', adjustmentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const transaction = docSnap.data() as InventoryTransaction;
      
      // Get product details from products collection
      const productRef = doc(db, 'products', transaction.productId);
      const productSnap = await getDoc(productRef);
      const productData = productSnap.exists() ? productSnap.data() : { name: 'Unknown Product', sku: 'N/A', averageCost: 0 };
      
      // Also try to get cost data from productCosts collection for more accuracy
      const productCostRef = doc(db, 'productCosts', transaction.productId);
      const productCostSnap = await getDoc(productCostRef);
      const productCostData = productCostSnap.exists() ? productCostSnap.data() : null;
      
      // Use the most accurate cost available, with a minimum default of 10
      const effectiveAverageCost = (productCostData?.averageCost || productData.averageCost || 10);
      
      // Calculate quantity change
      const quantityChange = transaction.type === 'addition' ? transaction.quantity : -transaction.quantity;
      
      // Different value calculation based on transaction type
      let valueChange = 0;
      
      if (transaction.type === 'reduction') {
        // For reductions (write-offs, damages, missing items), show financial impact
        // This represents the cost to the business
        valueChange = quantityChange * effectiveAverageCost;
      } else if (transaction.reason && transaction.reason.includes('Purchase Receipt')) {
        // For purchase receipts, calculate the value change based on average cost
        valueChange = quantityChange * effectiveAverageCost;
      }
      // For free product additions and other additions, keep value change as 0
      
      // Map to adjustment format
      const adjustment: InventoryAdjustment = {
        id: docSnap.id,
        adjustmentDate: transaction.date instanceof Timestamp 
          ? transaction.date.toDate() 
          : transaction.date,
        adjustmentNumber: docSnap.id.substring(0, 8).toUpperCase(),
        reason: transaction.reason,
        totalValueChange: valueChange,
        createdBy: transaction.performedBy,
        items: [{
          productId: transaction.productId,
          productName: productData.name || 'Unknown Product',
          productSku: productData.sku || 'N/A',
          quantityBefore: transaction.previousStock,
          quantityAfter: transaction.newStock,
          quantityChange: quantityChange,
          averageCost: productData.averageCost || 0,
          valueChange: valueChange
        }]
      };
      
      return adjustment;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting inventory adjustment:', error);
    throw error;
  }
};

// GL Transaction Entry interface
export interface GLTransactionEntry {
  accountId: string;
  accountNumber: string;
  accountName: string;
  debit: number;
  credit: number;
}

// GL Transaction interface
export interface GLTransaction {
  id: string;
  transactionDate: Date | Timestamp;
  description: string;
  referenceType: string;
  referenceId: string;
  entries: GLTransactionEntry[];
}

// Get related GL transaction for an inventory adjustment
// Note: Since manual adjustments don't create GL entries in the current implementation,
// this function will simulate GL entries for display purposes
export const getRelatedGLTransaction = async (adjustmentId: string): Promise<GLTransaction | null> => {
  try {
    // Get the adjustment first
    const adjustment = await getInventoryAdjustmentById(adjustmentId);
    
    if (!adjustment) {
      return null;
    }
    
    // Create a simulated GL transaction for display
    // This doesn't exist in the database but helps maintain UI consistency
    const simulatedGLTransaction: GLTransaction = {
      id: 'simulated-' + adjustmentId,
      transactionDate: adjustment.adjustmentDate,
      description: `Inventory ${adjustment.totalValueChange >= 0 ? 'Addition' : 'Reduction'}: ${adjustment.reason}`,
      referenceType: 'inventory_transaction',
      referenceId: adjustmentId,
      entries: []
    };
    
    // Determine account names based on adjustment reason
    let expenseAccountName = 'Inventory Adjustments';
    let expenseAccountNumber = '4900000';
    
    // Customize the account based on the reason
    if (adjustment.reason === 'Missing Items') {
      expenseAccountName = 'Inventory Shrinkage';
      expenseAccountNumber = '4910000';
    } else if (adjustment.reason === 'Damaged Product' || adjustment.reason === 'Expired Product') {
      expenseAccountName = 'Inventory Write-off';
      expenseAccountNumber = '4920000';
    } else if (adjustment.reason === 'Consumed for Salon Use' || adjustment.reason === 'Staff Usage') {
      expenseAccountName = 'Salon Supplies Expense';
      expenseAccountNumber = '7100000';
    }
    
    // For GL entries, show financial impact for purchase receipts and reductions
    // Only free product additions and other non-purchase additions have zero value
    let effectiveValue = 0;
    
    // If it's a reduction (negative value change) or a purchase receipt, show financial impact
    if (adjustment.totalValueChange < 0 || (adjustment.reason && adjustment.reason.includes('Purchase Receipt'))) {
      effectiveValue = Math.abs(adjustment.totalValueChange);
    }
    
    // If the value is still zero but it's a reduction, use a default value based on quantity
    if (effectiveValue === 0 && adjustment.items[0]?.quantityChange < 0) {
      effectiveValue = Math.abs(adjustment.items[0].quantityChange) * 10; // Default value of 10 per unit
    }
    
    // Add appropriate entries based on whether it's an addition or reduction
    if (adjustment.totalValueChange >= 0) {
      // For positive adjustments (additions)
      simulatedGLTransaction.entries = [
        {
          accountId: 'inventory',
          accountNumber: '1300000',
          accountName: 'Inventory Asset',
          debit: effectiveValue,
          credit: 0
        },
        {
          accountId: 'adjustment',
          accountNumber: '4900000',
          accountName: 'Inventory Adjustments',
          debit: 0,
          credit: effectiveValue
        }
      ];
    } else {
      // For negative adjustments (reductions)
      simulatedGLTransaction.entries = [
        {
          accountId: 'expense',
          accountNumber: expenseAccountNumber,
          accountName: expenseAccountName,
          debit: effectiveValue,
          credit: 0
        },
        {
          accountId: 'inventory',
          accountNumber: '1300000',
          accountName: 'Inventory Asset',
          debit: 0,
          credit: effectiveValue
        }
      ];
    }
    
    return simulatedGLTransaction;
  } catch (error) {
    console.error('Error getting related GL transaction:', error);
    throw error;
  }
};
